import { useCallback, useMemo, useState, type ReactNode } from 'react';
import type {
  AppError,
  AuthnProps,
  EventFunction,
} from '@imprivata-cloud/authn';
import { Authn, AuthnMethod, EventType } from '@imprivata-cloud/authn';
import { Icon } from '@imprivata-cloud/design-system';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { BannerProps } from './Banner/banner';
import { getWorkflowId } from '../../tracing';
import { AuthnLayout, LayoutHeaders } from './Card/authn-layout';
import DefaultLogo from '../../assets/default_logo.svg?react';
import ProxHex from '../../assets/proxHex.svg?react';
import { openUss } from '../../utils/utils';
import { ErrorView } from '../../components';

type AuthProps = Omit<
  AuthnProps,
  'tenantId' | 'tracing' | 'showCantLoginButton'
> & {
  banner?: BannerProps;
  fatalError?: AppError;
};

const queryClient = new QueryClient();

const hexImages: Record<AuthnMethod, ReactNode> = {
  [AuthnMethod.FACE_ID]: <Icon name="face-bio" />,
  [AuthnMethod.PROX]: <ProxHex />,
  [AuthnMethod.PASSWORD]: <Icon name="username" />,
  [AuthnMethod.IMPR_ID]: <Icon name="iid" />,
  [AuthnMethod.IMPR_PIN]: <Icon name="pin" />,
  [AuthnMethod.IID_DISABLED]: <Icon name="iid" />,
  [AuthnMethod.TEMP_CODE]: <Icon name="temp-code" />,
  [AuthnMethod.EMAIL_OTP]: <Icon name="one-time-pc" />,
};

export const Auth = ({
  banner,
  fatalError,
  orgPreferences,
  onEvent,
  ...authnProps
}: AuthProps) => {
  const [authnMethod, setAuthnMethod] = useState<AuthnMethod>();
  const handleEvent = useCallback<EventFunction>(
    payload => {
      if (payload.event === EventType.START_AUTHENTICATING) {
        setAuthnMethod(payload.data);
      }
      // A factor unmounting without a successor (e.g. prox -> username form)
      // leaves no method, so the header falls back to its neutral glyph.
      if (payload.event === EventType.FINISH_AUTHENTICATING) {
        const finished = payload.data;
        setAuthnMethod(current => (current === finished ? undefined : current));
      }
      onEvent(payload);
    },
    [onEvent],
  );
  const tenantId = new URL(window.location.href).searchParams.get('tenantId');

  const tracing = useMemo(
    () => ({
      workflowId: getWorkflowId() ?? '',
    }),
    [],
  );

  if (!tenantId) {
    console.error('[AppAccess] Missing tenantId URL param!');
    return <></>;
  }

  let content: ReactNode = null;
  if (fatalError) {
    content = <ErrorView error={fatalError} />;
  } else {
    content = (
      <QueryClientProvider client={queryClient}>
        <Authn
          {...authnProps}
          onEvent={handleEvent}
          tenantId={tenantId}
          tracing={tracing}
          orgPreferences={orgPreferences}
          openUSSOverride={openUss}
          showCantLoginButton={false}
          autoComplete="off"
        />
      </QueryClientProvider>
    );
  }

  return (
    <AuthnLayout
      banner={banner}
      header={LayoutHeaders.AUTHENTICATION}
      logoUrl={orgPreferences?.logoUrl || <DefaultLogo />}
      hexImage={authnMethod && hexImages[authnMethod]}
    >
      {content}
    </AuthnLayout>
  );
};
